/**
 * ProfileJobCard — job card on the My Jobs tabs (Applied, Invited, Recommended, Saved).
 * Mirrors Flutter profile_job_card.dart (ProfileJobCard, _ApplyButtonWidget, _ApplicationStatusWidget):
 *   - title (long-press copies it), relevance match (Recommended only), save / share
 *   - Job ID (long-press copies it), location, experience, skills (own line), posted date
 *   - "Updated on" / "Invited on" / "Saved on" date (not on Recommended)
 *   - application status + Easy Apply / Apply / Resume Application / Applied (edit) actions.
 *     With the list/detail split (≥1024px) the actions live in the preview panel instead.
 * Deviations kept from React (D2): the Invited tab shows the job's real location (Flutter
 * hard-codes 'N/A'), and share uses job.id like every other card (Flutter My Jobs uses displayId).
 */
import { useTranslation } from 'react-i18next';
import { useTenantStore } from '@/store/tenantStore';
import { useLocalizationStore } from '@/store/localizationStore';
import { EasyApplyButton } from '@/features/jobs/components/EasyApplyButton';
import { OnboardingJobButton } from '@/features/onboarding/entry';
import {
  CalendarIcon,
  ClockIcon,
  ExperienceIcon,
  FlagIcon,
  JobCardFooter,
  JobCardFrame,
  JobCardHead,
  JobFact,
  JobFacts,
  LocationIcon,
  SkillsIcon,
  jobCardStyles,
  stopThen,
  type JobTitleLink,
} from '@/features/jobs/components/JobCardParts';
import { jobCardFields } from '@/features/home/components/HomeJobListItem';
import { formatDate } from '@/features/jobs/utils/jobHelpers';
import { useLongPressCopy } from '@/features/jobs/utils/longPressCopy';
import { ViewJobsTab } from '@/types/profile.types';
import type { ViewJobsData } from '@/types/profile.types';
import styles from './ProfileJobCard.module.css';

/** Flutter OtherConstants.appliedType */
const EASY_APPLIED = 'EASY_APPLIED';
const DRAFT = 'DRAFT';

/** Flutter ProfileJobCard flags, derived from the job's appliedStatus. */
export function applicationState(job: ViewJobsData) {
  const status = job.appliedStatus ?? '';
  return {
    /** No application yet — Apply / Easy Apply */
    isStartApply: !status,
    /** Submitted (anything but a draft) */
    isJobApplied: status !== DRAFT,
    /** Easy-applied applications can't be edited */
    isEasyAppliedStatus: status === EASY_APPLIED,
  };
}

const DATE_LABEL: Partial<Record<ViewJobsTab, string>> = {
  [ViewJobsTab.APPLIED]: 'Updated on',
  [ViewJobsTab.INVITED]: 'Invited on',
  [ViewJobsTab.SAVED]: 'Saved on',
};

/** Flutter _ApplicationStatusWidget — "Status : <displayableStatus | Applied | Pending>". */
export function ApplicationStatusPill({ job }: { job: ViewJobsData }) {
  const { t } = useTranslation();
  const { isStartApply, isJobApplied } = applicationState(job);
  if (isStartApply) return null;

  const displayable = job.stepStatus?.displayableStatus;
  const isPositive = !!displayable || isJobApplied;
  return (
    <span className={`${styles.status} ${isPositive ? styles.statusPositive : styles.statusPending}`}>
      <span className={styles.statusLabel}>{t('Status')}:</span>
      <span className={styles.statusValue}>{displayable || (isJobApplied ? t('Applied') : t('Pending'))}</span>
    </span>
  );
}

/**
 * Flutter _ApplyButtonWidget. `onApply(isEasyApply)` opens the (easy) application form.
 * Apply / Resume Application use primaryColorDark (--accent); Applied is green.
 */
export function ApplicationActions({
  job,
  onApply,
}: {
  job: ViewJobsData;
  onApply: (isEasyApply: boolean) => void;
}) {
  const { t } = useTranslation();
  const config = useTenantStore((s) => s.config);
  const preferences = useTenantStore((s) => s.preferences);
  const enableEasyApply = !!(config as Record<string, unknown> | null)?.enableEasyApply;
  const resumeTooltip = (preferences as Record<string, unknown> | null)?.resumeApplicationTooltipMsg as
    | string
    | undefined;
  const { isStartApply, isJobApplied, isEasyAppliedStatus } = applicationState(job);

  if (isStartApply) {
    return (
      <>
        {enableEasyApply && <EasyApplyButton onClick={stopThen(() => onApply(true))} />}
        <button
          type="button"
          className={`${jobCardStyles.applyBtn} ${styles.accentBtn}`}
          onClick={stopThen(() => onApply(false))}
        >
          {t('Apply')}
        </button>
      </>
    );
  }

  if (isJobApplied) {
    return (
      <>
        <button
          type="button"
          className={styles.appliedBtn}
          onClick={stopThen(() => onApply(false))}
          disabled={isEasyAppliedStatus}
          title={isEasyAppliedStatus ? undefined : t('Edit')}
        >
          {t('Applied')}
          {!isEasyAppliedStatus && (
            <svg width="14" height="14" viewBox="0 0 20 20" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
              <path d="M12.5 4.5l3 3L7 16H4v-3l8.5-8.5Z" />
            </svg>
          )}
        </button>
        {/* Candidate onboarding (React only, FLUTTER_DEVIATIONS §5): the onboarding form filled from this application,
            only on a job the candidate is hired for (the HiredRequisitionsProvider around My Jobs); nothing in prod */}
        <OnboardingJobButton requisitionId={job.id} requisitionDisplayId={job.displayId} className={styles.onboardingBtn} label={t('Onboarding In Progress')} />
      </>
    );
  }

  // Draft — Flutter "Resume Application" with the tenant tooltip
  return (
    <button
      type="button"
      className={`${jobCardStyles.applyBtn} ${styles.accentBtn} ${styles.resumeBtn}`}
      onClick={stopThen(() => onApply(false))}
      title={resumeTooltip ? t(resumeTooltip) : undefined}
    >
      <svg width="12" height="12" viewBox="0 0 20 20" fill="currentColor" aria-hidden="true">
        <path d="M6 3.8v12.4a1 1 0 0 0 1.5.86l10-6.2a1 1 0 0 0 0-1.72l-10-6.2A1 1 0 0 0 6 3.8Z" />
      </svg>
      {t('Resume Application')}
    </button>
  );
}

interface ProfileJobCardProps {
  job: ViewJobsData;
  tab: ViewJobsTab;
  isSelected: boolean;
  onSelect: () => void;
  onSave: () => void;
  onUnsave: () => void;
  /** Card-level apply actions (Flutter _ApplyButtonWidget); omitted when the preview panel has them. */
  onApply?: (isEasyApply: boolean) => void;
  /** The title links to the job page */
  titleLink?: JobTitleLink;
}

export function ProfileJobCard({
  job,
  tab,
  isSelected,
  onSelect,
  onSave,
  onUnsave,
  onApply,
  titleLink,
}: ProfileJobCardProps) {
  const { t } = useTranslation();
  const language = useLocalizationStore((s) => s.currentLanguage);
  const { displayId, location, skills, experience, postedDate } = jobCardFields(job, t);
  const longPressJobId = useLongPressCopy(displayId);
  const { isStartApply, isJobApplied } = applicationState(job);
  const isRecommended = tab === ViewJobsTab.RECOMMENDED;
  const dateLabel = DATE_LABEL[tab];
  const relevanceLevel =
    isRecommended && job.relevance && 'level' in job.relevance ? job.relevance.level : undefined;

  return (
    <JobCardFrame isSelected={isSelected} onSelect={onSelect}>
      <JobCardHead
        title={job.jobTitle}
        displayId={displayId}
        jobId={job.id}
        relevanceLevel={relevanceLevel}
        isSaved={job.isSavedJob}
        isSaving={job.isLoadingForSaveJobButton}
        onSave={onSave}
        onUnsave={onUnsave}
        copyTitleOnLongPress
        titleLink={titleLink}
      />

      <JobFacts>
        <JobFact icon={<FlagIcon />} label={t('Job ID')}>
          <span {...longPressJobId}>
            {t('Job ID')} {displayId}
          </span>
        </JobFact>
        <JobFact icon={<LocationIcon />} label={t('Location')}>
          {location}
        </JobFact>
        <JobFact icon={<ExperienceIcon />} label={t('Experience')}>
          {experience}
        </JobFact>
        <JobFact icon={<SkillsIcon />} label={t('Skills')} tooltip={skills} wide>
          {skills}
        </JobFact>
        <JobFact icon={<CalendarIcon />} label={t('Posted on')}>
          {t('Posted')} {postedDate}
        </JobFact>
        {dateLabel && (
          <JobFact icon={<ClockIcon />} label={t(dateLabel)}>
            {t(dateLabel)} : {formatDate(job.updatedOn, language)}
          </JobFact>
        )}
      </JobFacts>

      {(!isStartApply || onApply) && (
        <JobCardFooter start={<ApplicationStatusPill job={job} />}>
          {onApply ? (
            <ApplicationActions job={job} onApply={onApply} />
          ) : (
            // Side by side the actions live in the detail panel, but the card keeps the way into onboarding
            // (React only, FLUTTER_DEVIATIONS §5): only on a job the candidate is hired for; nothing in prod
            isJobApplied && (
              <OnboardingJobButton
                requisitionId={job.id}
                requisitionDisplayId={job.displayId}
                className={styles.onboardingBtn}
                label={t('Onboarding In Progress')}
              />
            )
          )}
        </JobCardFooter>
      )}
    </JobCardFrame>
  );
}
